import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, AlertSeverity } from '../components/ui/Alert';

/**
 * Lightweight, app-wide toast system — replaces native browser alerts.
 * Each toast renders with the shared <Alert /> component (Tailwind, DESIGN.md),
 * stacks bottom-right, and auto-dismisses. Messages must already be translated
 * (pass `t('key')`), never raw strings.
 */
interface Toast {
  id: number;
  message: string;
  severity: AlertSeverity;
}

interface ToastContextType {
  /** Show a transient toast. Defaults to the `error` severity. */
  showToast: (message: string, severity?: AlertSeverity) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  /** Auto-dismiss delay in ms. */
  duration?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, duration = 5000 }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message: string, severity: AlertSeverity = 'error') => {
      const id = (idRef.current += 1);
      setToasts((prev) => [...prev, { id, message, severity }]);
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    },
    [dismiss, duration],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[1500] flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto animate-slide-up rounded-xl bg-surface shadow-pop"
          >
            <Alert severity={toast.severity} onClose={() => dismiss(toast.id)}>
              {toast.message}
            </Alert>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
